import { saveToLocalStorage } from '@kmx/io-core/storage';
import { useCallback, useMemo } from 'react';
import { useOfferContext } from '../../context/offerContext';
import { useStoreInfo } from '../../context/storeInfo';
import { useVehicleInfo } from '../../context/vehicleInfo';
import { IContinuation } from '../../types/IOfferResponse';
import { defaultIneligibleParams, vehicleIneligible } from '../../utils/offerEvents';
import { profileComplete } from '../../utils/validation';

interface IVehicleIneligibleParams {
    isSecondIneligible?: boolean;
    mileage?: number;
    continuation?: IContinuation;
    reason?: string;
    pageType?: string;
    isPicsyEligible?: boolean;
    picsyUrl?: string;
}

interface IUseVehicleIneligible {
    onIneligible?: () => void;
}

export default function useTriggerIneligibleEvent({ onIneligible }: IUseVehicleIneligible = {}) {
    const { handleIneligible, quoteId, saveResult, setIneligible } = useOfferContext();
    const { storeInfo } = useStoreInfo();
    const { vehicleInfo } = useVehicleInfo();

    const trigger = useCallback(
        (params: IVehicleIneligibleParams) => {
            const parameter = {
                ...defaultIneligibleParams,
                saveResult,
                quoteId,
                vehicleInfo,
                ...params,
            };

            vehicleIneligible(parameter);

            if (saveResult && storeInfo && (profileComplete(vehicleInfo) || quoteId)) {
                saveToLocalStorage({
                    createdDateUtc: new Date(),
                    expirationDateUtc: new Date(new Date().setDate(new Date().getDate() + 7)),
                    isPicsyEligible: parameter.isPicsyEligible ? true : false,
                    offerAmount: 0,
                    offerCode: quoteId,
                    make: vehicleInfo.profile.make,
                    mileage: parameter.mileage ? parameter.mileage : vehicleInfo.mileage,
                    model: vehicleInfo.profile.model,
                    picsyUrl: params.picsyUrl,
                    storeId: storeInfo.id,
                    trim: vehicleInfo.profile.trim,
                    vin: vehicleInfo.vin,
                    year: vehicleInfo.profile.year,
                    zipCode: vehicleInfo.zipcode,
                });
            }

            if (handleIneligible) {
                setIneligible(true);
            }

            if (typeof onIneligible === 'function') {
                onIneligible();
            }
        },
        [saveResult, quoteId, vehicleInfo, setIneligible, onIneligible, handleIneligible]
    );

    return useMemo(() => ({ trigger }), [trigger]);
}
